if (!customElements.get('preorder-verification-popup')) {
  customElements.define(
    'preorder-verification-popup',
    class PreorderVerificationPopup extends HTMLElement {
      connectedCallback() {
        if (this.moved) return;
        this.moved = true;
        document.body.appendChild(this);

        this.layout = this.dataset.layout || 'default';
        // Layout 2 uses role="status" (no dialog), default uses role="dialog"
        this.dialogHolder = this.querySelector('[role="dialog"]') || this.querySelector('[role="status"]');

        // Close button
        this.querySelector('[id^="ModalClose-"]')
          ?.addEventListener('click', this.hide.bind(this));

        // ESC key
        this.addEventListener('keyup', (e) => {
          if (e.code?.toUpperCase() === 'ESCAPE') this.hide();
        });

        if (this.layout === 'vg-layout-2') {
          // Layout 2: non-blocking toast — no confirm/decline, backdrop, or focus trap
          requestAnimationFrame(() => this.setupLayout2());
        } else {
          // Default: blocking modal
          this.querySelector('.preorder-popup__confirm')
            ?.addEventListener('click', this.onConfirm.bind(this));
          this.querySelector('.preorder-popup__decline')
            ?.addEventListener('click', this.hide.bind(this));

          // Backdrop click closes modal
          this.addEventListener('click', (e) => {
            if (e.target === this) this.hide();
          });

          // Prevent clicks inside dialog from bubbling to backdrop
          this.dialogHolder?.addEventListener('click', (e) => e.stopPropagation());

          requestAnimationFrame(() => this.setupInterception());
        }
      }

      // ─── Layout 2 setup ─────────────────────────────────────────────────

      setupLayout2() {
        const sectionId = this.dataset.sectionId;
        this.productForm = document.querySelector(
          `product-form[data-section-id="${sectionId}"]`
        );
        if (!this.productForm) return;

        // Show toast on button click (non-blocking — form submit proceeds normally)
        this.productForm.addEventListener('click', (e) => {
          const btn = e.target.closest('.product-form__submit');
          if (!btn || !btn.hasAttribute('data-preorder')) return;
          this.show();
        });

        // Hide toast when user switches to a non-preorder variant
        subscribe('variant-change', () => {
          const btn = this.productForm?.querySelector('.product-form__submit');
          if (!btn?.hasAttribute('data-preorder')) this.hide();
        });
      }

      // ─── Default modal interception ──────────────────────────────────────

      setupInterception() {
        const sectionId = this.dataset.sectionId;
        this.productForm = document.querySelector(
          `product-form[data-section-id="${sectionId}"]`
        );
        if (!this.productForm) return;

        const form = this.productForm.querySelector('form');
        form?.addEventListener('submit', this.interceptSubmit.bind(this), true);

        this.setupPaymentButtonOverlay();
        subscribe('variant-change', () => this.syncPaymentOverlay());
      }

      interceptSubmit(evt) {
        if (this.confirmed) {
          this.confirmed = false;
          return;
        }
        const submitBtn =
          evt.submitter ||
          this.productForm?.querySelector('.product-form__submit');
        if (!submitBtn?.hasAttribute('data-preorder')) return;

        evt.preventDefault();
        evt.stopImmediatePropagation();
        this.pendingSubmitter = evt.submitter;
        this.pendingIsPayment = false;
        this.show();
      }

      // ─── Payment button overlay ──────────────────────────────────────────

      setupPaymentButtonOverlay() {
        const container = this.productForm?.querySelector('.shopify-payment-button');
        if (!container) return;

        const overlay = document.createElement('div');
        overlay.className = 'preorder-popup__payment-overlay';
        container.style.position = 'relative';
        container.appendChild(overlay);

        overlay.addEventListener('click', () => {
          const submitBtn = this.productForm?.querySelector('.product-form__submit');
          if (!submitBtn?.hasAttribute('data-preorder')) return;
          this.pendingIsPayment = true;
          this.pendingSubmitter = null;
          this.show();
        });

        this.syncPaymentOverlay();
      }

      syncPaymentOverlay() {
        const overlay = this.productForm?.querySelector('.preorder-popup__payment-overlay');
        if (!overlay) return;
        const submitBtn = this.productForm?.querySelector('.product-form__submit');
        overlay.style.pointerEvents = submitBtn?.hasAttribute('data-preorder') ? '' : 'none';
      }

      // ─── Quick-cart re-init ──────────────────────────────────────────
      // Called from quick-cart-drawer.js after AJAX content is inserted so
      // the popup can bind to the form that wasn't in the DOM on connectedCallback.
      reinit() {
        this.productForm = null;
        this.confirmed = false;
        requestAnimationFrame(() => {
          if (this.layout === 'vg-layout-2') {
            this.setupLayout2();
          } else {
            this.setupInterception();
          }
        });
      }

      // ─── Show / Hide ─────────────────────────────────────────────────────

      show() {
        this.setAttribute('open', '');
        if (this.layout !== 'vg-layout-2') {
          if (typeof bodyScroll !== 'undefined') bodyScroll.lock(this.dialogHolder);
          if (typeof trapFocus !== 'undefined') trapFocus(this, this.dialogHolder);
        }
      }

      hide() {
        this.removeAttribute('open');
        if (this.layout !== 'vg-layout-2') {
          if (typeof bodyScroll !== 'undefined') bodyScroll.unlock(this.dialogHolder);
          if (typeof removeTrapFocus !== 'undefined') removeTrapFocus();
        }
      }

      // ─── Confirm (default modal only) ────────────────────────────────────

      onConfirm() {
        this.hide();

        if (this.pendingIsPayment) {
          const overlay = this.productForm?.querySelector('.preorder-popup__payment-overlay');
          if (overlay) {
            overlay.style.pointerEvents = 'none';
            const paymentBtn = this.productForm?.querySelector('.shopify-payment-button__button');
            paymentBtn?.click();
            setTimeout(() => this.syncPaymentOverlay(), 1000);
          }
          return;
        }

        this.confirmed = true;
        const form = this.productForm?.querySelector('form');
        if (!form) return;
        try {
          this.pendingSubmitter
            ? form.requestSubmit(this.pendingSubmitter)
            : form.requestSubmit();
        } catch {
          form.submit();
        }
      }
    }
  );
}
